import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, NumberInput, Toggle } from '@carbon/react';
import { api, ApiError } from '../api/client';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { CommandStatus, HE_A_ERRORS, WAGERING_SOURCE } from '../components/labels';
import type { Command } from './Commands';

type Source = 'giftcode' | 'su_kien' | 'hoan_tra' | 'den_bu';
type Doc = { heSo: Partial<Record<Source, number>>; gameKhongTinh: string[]; tyLe?: Record<string, number>; chanPoker?: boolean };
type Data = { noiDung: Doc; games: string[]; meta: { version: number; source: string; at: string | null }; pending: { id: string; status: string }[] };

const SOURCES: { key: Source; help: string }[] = [
  { key: 'giftcode', help: 'Mã giftcode người chơi nhập.' },
  { key: 'su_kien', help: 'Thưởng nhận từ sự kiện và nhiệm vụ.' },
  { key: 'hoan_tra', help: 'Tiền hoàn trả theo cấp VIP (khi bật).' },
  { key: 'den_bu', help: 'Lệnh cộng tiền của quản trị viên có đánh dấu vòng cược.' },
];

const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';

export function WageringPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['wagering-config'], queryFn: () => api.get<Data>('/api/admin/wallet/wagering') });
  // ban nhap giu phien ban luc bat dau sua: tai lai trang khong doi no (tranh ghi de nguoi khac)
  const [draft, setDraft] = useState<{ doc: Doc; version: number } | null>(null);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'warning'; title: string; subtitle: string } | null>(null);
  const live: Doc = draft?.doc ?? q.data?.noiDung ?? { heSo: {}, gameKhongTinh: [] };
  const set = (p: Partial<Doc>) => setDraft({ doc: { ...live, ...p }, version: draft?.version ?? q.data?.meta.version ?? 0 });
  const reload = () => {
    setDraft(null); setOpen(false); qc.invalidateQueries({ queryKey: ['wagering-config'] });
    setNotice({ kind: 'warning', title: 'Cấu hình vừa được người khác lưu', subtitle: 'Thay đổi của bạn chưa được lưu. Trang đã tải lại bản mới nhất, hãy sửa lại.' });
  };
  const save = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/wallet/wagering', {
      // ty le day du moi game; game bo chon o ban truoc da la 0 trong tyLe nen gameKhongTinh rong
      noiDung: { heSo: live.heSo, tyLe: rates, gameKhongTinh: [], chanPoker }, expect: draft?.version ?? q.data?.meta.version ?? 0,
    }),
    onSuccess: (r) => {
      const c = r.command;
      if (c.status === 'FAILED' && c.lastError === 'PHIEN_BAN_CU') return reload();
      setDraft(null); setOpen(false); qc.invalidateQueries({ queryKey: ['wagering-config'] });
      setNotice(c.status === 'SUCCEEDED'
        ? { kind: 'success', title: 'Hệ A đã nhận cấu hình mới', subtitle: 'Áp dụng cho tiền thưởng cộng từ giờ và cho mọi cược tiếp theo.' }
        : { kind: 'warning', title: 'Lệnh chưa xong', subtitle: (c.lastError && HE_A_ERRORS[c.lastError]) || c.lastError || c.status });
    },
    onError: (e) => { if (isStale(e)) reload(); },
  });

  const games = q.data?.games ?? [];
  const chanPoker = live.chanPoker !== false;
  // ty le dang ap dung cua moi game (he A tra hieu_luc.tyLe day du; game bo chon o trang cu = 0)
  const off = new Set(live.gameKhongTinh ?? []);
  const rates: Record<string, number> = Object.fromEntries(games.map((g) => [g, off.has(g) ? 0 : live.tyLe?.[g] ?? 100]));
  const setRate = (g: string, v: number) => set({ tyLe: { ...rates, [g]: Math.min(100, Math.max(0, Math.round(v) || 0)) }, gameKhongTinh: [] });
  const notFull = games.filter((g) => rates[g] !== 100);

  return (
    <div className="page">
      <PageHeader title="Vòng cược trước khi rút"
        lead="Tiền thưởng cộng kèm một yêu cầu cược: số tiền thưởng nhân hệ số. Mỗi cược tiền thật trừ vào yêu cầu theo tỷ lệ % của game đó; cược bị hoàn trả lại đúng phần đã tính. Người chơi chỉ rút được khi yêu cầu về 0. Tỷ lệ áp dụng cho cả vòng cược tiền thưởng và vòng cược tiền nạp (hệ số nạp nằm ở trang Tài khoản nhận và hạn mức). Cược xu và bot không bao giờ được tính." />
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : (
        <>
          <p className="muted">Phiên bản {q.data?.meta.version} ({q.data?.meta.source === 'cms' ? 'CMS' : 'mặc định'}){q.data?.meta.at ? <> · <When v={q.data.meta.at} /></> : null}</p>
          {notice ? <InlineNotification kind={notice.kind} lowContrast title={notice.title} subtitle={notice.subtitle} onClose={() => setNotice(null)} /> : null}
          {q.data?.pending.length ? <p>Có lệnh đang chờ: {q.data.pending.map((p) => <CommandStatus key={p.id} s={p.status} />)}</p> : null}
          <section className="section">
            <h2>Hệ số theo nguồn tiền thưởng</h2>
            <div className="stack">
              {SOURCES.map((s) => (
                <NumberInput key={s.key} id={'hs-' + s.key} label={WAGERING_SOURCE[s.key] + ': số lần tiền thưởng phải cược'}
                  helperText={s.help + ' 0 = không yêu cầu.'} min={0} max={100} step={0.5} value={Number(live.heSo[s.key] ?? 0)}
                  onChange={(_e, { value }) => set({ heSo: { ...live.heSo, [s.key]: Math.min(100, Math.max(0, Number(value) || 0)) } })} />
              ))}
            </div>
          </section>
          <section className="section">
            <h2>Tỷ lệ tính vòng cược theo game</h2>
            <p className="muted">Phần trăm tiền cược của mỗi game được tính vào vòng cược (mặc định 100%). 0 = không tính. Ví dụ Live Casino 10%: cược 1.000.000 tính 100.000. Vòng cược tiền thưởng: tỷ lệ mới áp dụng cho cược đặt từ lúc lưu, cược đã đặt giữ phần đã tính. Vòng cược tiền nạp tính lại từ sổ giao dịch theo tỷ lệ đang áp dụng, nên đổi tỷ lệ cũng tính lại cược cũ của vòng cược tiền nạp.</p>
            <div className="check-grid">
              {games.map((g) => (
                <NumberInput key={g} id={'tl-' + g} label={g + ' (%)'} min={0} max={100} step={1} allowEmpty={false}
                  value={rates[g]} invalidText="Từ 0 đến 100"
                  onChange={(_e, { value }) => setRate(g, Number(value))} />
              ))}
            </div>
          </section>
          <section className="section">
            <h2>Poker người đấu người</h2>
            <Toggle id="chan-poker" labelText="Chặn mua chip Poker khi còn vòng cược"
              labelA="Tắt: tiền thưởng chơi Poker được" labelB="Bật: còn vòng cược thì không vào bàn Poker"
              toggled={chanPoker} onToggle={(v) => set({ chanPoker: v })} />
            <p className="muted">Poker là tiền giữa người chơi với nhau (chip vào pot tính vòng cược theo tỷ lệ Poker ở trên), nên tiền thưởng chưa đủ vòng có thể bị chuyển sang tài khoản khác qua bàn chơi. Bật: người còn vòng cược không mua chip Poker được (đã ngồi thì vẫn đổi chip về ví được).</p>
          </section>
          <div className="row section">
            <Button disabled={!draft} onClick={() => setOpen(true)}>Lưu</Button>
            {draft ? <Button kind="ghost" onClick={() => setDraft(null)}>Bỏ thay đổi</Button> : null}
          </div>
          <Modal open={open} modalHeading="Lưu cấu hình vòng cược?" primaryButtonText="Lưu" secondaryButtonText="Hủy"
            primaryButtonDisabled={save.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => save.mutate()}>
            <div className="stack">
              <p>Hệ số mới chỉ áp dụng cho tiền thưởng cộng từ giờ; yêu cầu đã cộng giữ nguyên. Tỷ lệ game: vòng cược tiền thưởng áp dụng cho cược đặt từ lúc lưu; vòng cược tiền nạp tính lại cả cược cũ theo tỷ lệ mới.</p>
              <p>{SOURCES.map((s) => WAGERING_SOURCE[s.key] + ' ' + (live.heSo[s.key] ?? 0) + 'x').join(' · ')} · {notFull.length ? 'Tỷ lệ khác 100%: ' + notFull.map((g) => g + ' ' + rates[g] + '%').join(', ') : 'Mọi game 100%'} · Poker khi còn vòng cược: {chanPoker ? 'chặn' : 'cho chơi'}</p>
              <InlineError error={save.error} />
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}
